<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, watch, type Ref } from 'vue';
import { NButton, NInput, useMessage } from 'naive-ui';
import type { FormValidationStatus } from 'naive-ui/es/form/src/interface';
import SVG from '@dts/shared/src/assets/svg';
import Typography from '@/components/TextFormat/FormatElements/Typography';
import { marked } from 'marked';
import { genStaticUrl } from '@dts/shared/src/utils/httpUrlConfig';
import DTS_AI_IMG from '@/assets/assistant/dts_new_loading.png';
import { useParentId } from '@/hooks/useParentId';
import { StepsID } from '@/view/Home/HomePage/components/TourSteps';
import { v4 as uuidv4 } from 'uuid';
import { fetchLLMAnswer } from '@dts/shared/src/utils/llm';
import LoadingUI from './LoadingUI.vue';
import { homeApi } from '@/api/modules/home';
import { assistantApi, type AssistantQaItem } from '@/api/modules/assistant';
import { getToken } from '@dts/shared/src/utils/system';
import { PRIMARY_COLOR } from '@dts/shared/src/config/color';

interface AssistantPromptQuestion {
  id?: number | string;
  questionContent: string;
  answerContent?: string;
}

type PromptOption = string | AssistantPromptQuestion;

interface ChatItem {
  content: string;
  pos: 'left' | 'right';
  type:
    | 'begin'
    | 'answer'
    | 'question'
    | 'system'
    | 'prompt'
    | 'sorry'
    | 'dislike'
    | 'dislikeReason'
    | 'firstMayAsk';
  feedback: '' | 'like' | 'dislike';
  historyId: number;
  promptDone: boolean;
  feedbackReason: string;
  promptOptions?: PromptOption[];
  isAIGenerated?: boolean;
}

const user = inject<Ref<string>>('username');
const username = computed(() => user?.value ?? '');

const showAssistantChat = ref(false);
const chatDisplay = ref(false);
const valueRef = ref('');
const assistantContent = ref<HTMLElement | null>(null);
const assistantBox = ref<HTMLElement | null>(null);
const inputStatus = ref<FormValidationStatus>('success');
const showAssistantLoading = ref(false);
const { cid } = useParentId();
const llmChat = ref('');
const llmLoading = ref(false);
const uuid = ref(uuidv4());
const feedbackReasonStorage = ref<Partial<ChatItem>>({});
const qaId = ref<number>(-1);
const message = useMessage();
const answerPause = ref(false);

const mayAskQuestions = ref<AssistantPromptQuestion[]>([]);
const chatList = ref<ChatItem[]>([
  {
    content: 'I‘m DA (Data & AI). Nice to meet you !',
    pos: 'left',
    type: 'begin',
    feedback: '',
    historyId: 0,
    promptDone: false,
    feedbackReason: '',
  },
  {
    content: '',
    pos: 'left',
    type: 'firstMayAsk',
    feedback: '',
    historyId: 0,
    promptDone: false,
    feedbackReason: '',
  },
]);

const themeOverrides = {
  fontSizeMedium: '0.16rem',
};

const parseMarked = (content: string) => marked.parse(content) as string;
const normalizePromptQuestions = (items?: AssistantQaItem[]): AssistantPromptQuestion[] =>
  (items ?? []).flatMap((item) =>
    typeof item.questionContent === 'string'
      ? [
          {
            id: item.id,
            questionContent: item.questionContent,
            answerContent: item.answerContent,
          },
        ]
      : [],
  );

const showChat = () => {
  homeApi.recordActionBrowse({
    buttonId: 82,
    entity: '',
    pageId: Number(cid.value),
    type: 7,
  });
  chatDisplay.value = true;
  setTimeout(() => {
    showAssistantChat.value = true;
  });
};

const closeChat = () => {
  showAssistantChat.value = false;
  setTimeout(() => {
    chatDisplay.value = false;
  }, 100);
};

const chatClose = () => {
  closeChat();
};

const toggleChat = (event: MouseEvent) => {
  if (showAssistantChat.value) {
    closeChat();
    return;
  }
  event.stopPropagation();
  showChat();
};

const addTargetToLinks = (content: string) =>
  content.replace(/<a href="([^"]+)">([^<]+)<\/a>/g, '<a href="$1" target="_blank">$2</a>');

const scrollToBottom = (behavior?: ScrollBehavior) => {
  nextTick(() => {
    if (!assistantContent.value || !assistantBox.value) {
      return;
    }

    assistantContent.value.scrollTo({
      top: assistantBox.value.scrollHeight,
      ...(behavior ? { behavior } : {}),
    });
  });
};

const messageCallback = (data: { answer?: string; hyperlink?: string[] }) => {
  if (data?.answer && !answerPause.value) {
    llmChat.value += data.answer;
    chatList.value[chatList.value.length - 1].content = parseMarked(llmChat.value);
  }

  if (!data?.answer && !(data?.hyperlink?.length ?? 0) && !answerPause.value) {
    chatList.value[chatList.value.length - 1].content = addTargetToLinks(
      parseMarked(llmChat.value),
    );
  }

  scrollToBottom();
};

const handleLLMInfo = (answer: string, historyId?: number) => {
  assistantApi.updateAskHistory({
    answer,
    historyId,
  });
};

const handleLLMTemplate = async (data: { contents: string; token?: string }) => {
  fetchLLMAnswer({
    askContent: data.contents,
    uuid: uuid.value,
    token: data.token || '',
    startCallback: async () => {
      chatList.value.push({
        content: '',
        pos: 'left',
        type: 'answer',
        promptOptions: [],
        historyId: 0,
        promptDone: false,
        feedback: '',
        feedbackReason: '',
        isAIGenerated: false,
      });
      const isAIGenerated = await assistantApi.getAnswerSource({ question: data.contents });
      chatList.value[chatList.value.length - 1].isAIGenerated = isAIGenerated;
    },
    finishCallback: (id) => {
      chatList.value[chatList.value.length - 1].historyId = id;
      handleLLMInfo(llmChat.value, id);
      llmChat.value = '';
      answerPause.value = false;
    },
    loadingCallback: (isLoading) => {
      llmLoading.value = isLoading;
    },
    errorCallback: (err) => {
      llmChat.value += err;
      chatList.value[chatList.value.length - 1].content =
        "Sorry, I haven't learned this part yet. Are you interested in these hot topics?";
      scrollToBottom();
    },
    messageCallback,
  });
};

const sendQuest = async () => {
  if (llmLoading.value) {
    message.warning('Please watting, the message is generating right now');
    return;
  }

  if (!valueRef.value.trim()) {
    inputStatus.value = 'error';
    valueRef.value = '';
    return;
  }

  inputStatus.value = 'success';
  showAssistantLoading.value = true;

  const tempMessage = valueRef.value;
  chatList.value.push({
    content: valueRef.value,
    pos: 'right',
    type: 'question',
    feedback: '',
    historyId: 0,
    promptDone: false,
    feedbackReason: '',
  });
  valueRef.value = '';

  try {
    homeApi.recordActionBrowse({
      buttonId: 83,
      entity: valueRef.value,
      pageId: Number(cid.value),
      type: 7,
    });

    const token = getToken();
    await handleLLMTemplate({
      token,
      contents: tempMessage,
    });
  } catch {
    valueRef.value = '';
    chatList.value.push({
      content: "Sorry, I haven't learned this part yet. Are you interested in these hot topics?",
      pos: 'left',
      type: 'sorry',
      feedback: '',
      historyId: 0,
      promptDone: false,
      feedbackReason: '',
    });
  } finally {
    valueRef.value = '';
    showAssistantLoading.value = false;
  }
};

const clickPromptOption = (item: ChatItem, value: string) => {
  if (item.type === 'prompt' || item.type === 'begin' || item.type === 'firstMayAsk') {
    valueRef.value = value;
    sendQuest();
    return;
  }

  if (item.type === 'dislike') {
    if (item.promptDone) {
      return;
    }

    if (value === 'Other') {
      chatList.value.push({
        content: 'Please enter the reason',
        pos: 'left',
        type: 'dislikeReason',
        feedback: '',
        historyId: 0,
        promptDone: false,
        feedbackReason: '',
      });
      item.promptDone = true;
      return;
    }

    feedbackReason(value, item);
    chatList.value.push({
      content: value,
      pos: 'right',
      type: 'system',
      feedback: '',
      historyId: 0,
      promptDone: false,
      feedbackReason: '',
    });
  }
};

const feedback = (type = true, item: ChatItem) => {
  if (item.feedback) {
    return;
  }

  homeApi.recordActionBrowse({
    buttonId: type ? 84 : 85,
    entity: '',
    pageId: Number(cid.value),
    type: 7,
  });

  assistantApi
    .submitFeedback({
      feedback: type,
      historyId: Number(item.historyId) || undefined,
    })
    .then(() => {
      item.feedback = type ? 'like' : 'dislike';
      if (!type) {
        chatList.value.push({
          content: 'Could you please tell me the reason for your dissatisfaction?',
          pos: 'left',
          type: 'dislike',
          promptOptions: ['Unrelated', 'Unclear', 'Contain sensitive information', 'Other'],
          feedback: '',
          historyId: 0,
          promptDone: false,
          feedbackReason: '',
        });
      }
    });
};

const handleDislike = (item: ChatItem) => {
  feedbackReasonStorage.value = item;
  feedback(false, item);
};

const feedbackReason = (reason: string, item: ChatItem) => {
  if (item.promptDone) {
    return;
  }

  item.promptDone = true;

  homeApi.recordActionBrowse({
    buttonId: 86,
    entity: reason,
    pageId: Number(cid.value),
    type: 7,
  });

  assistantApi
    .submitFeedbackReason({
      historyId: Number(feedbackReasonStorage.value.historyId) || undefined,
      reason,
    })
    .then(() => {
      chatList.value.push({
        content:
          'Thanks for your feedback. I will continue to grow up to provide you with better service.',
        pos: 'left',
        type: 'system',
        feedback: '',
        historyId: 0,
        promptDone: false,
        feedbackReason: '',
      });
      feedbackReasonStorage.value = {};
    });
};

const refreshQustion = () => {
  assistantApi.getPresetQuestions({ qaId: qaId.value }).then((data) => {
    qaId.value = Number(data?.[2]?.id) || -1;
    mayAskQuestions.value = normalizePromptQuestions(data);
  });
};

const stopReply = () => {
  answerPause.value = true;
  llmLoading.value = false;
};

const handleAutoCompleteKeydown = (event: KeyboardEvent) => {
  // Ignore Enter while IME composition is still active.
  if (event.isComposing || event.keyCode === 229) {
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    sendQuest();
  }
};

const getPromptKey = (option: PromptOption, index: number) =>
  typeof option === 'string'
    ? `${option}-${index}`
    : `${option.id ?? option.questionContent}-${index}`;

const getPromptLabel = (option: PromptOption) =>
  typeof option === 'string' ? option : option.questionContent;

const shouldShowLoading = (item: ChatItem, index: number) =>
  (showAssistantLoading.value || llmLoading.value) &&
  index === chatList.value.length - 1 &&
  item.pos === 'left';

const shouldShowGenerated = (item: ChatItem, index: number) =>
  Boolean(item.isAIGenerated) &&
  item.pos === 'left' &&
  !(index === chatList.value.length - 1 && (showAssistantLoading.value || llmLoading.value));

onMounted(() => {
  refreshQustion();
});

watch(
  () => showAssistantChat.value,
  () => {
    scrollToBottom();
  },
);

watch(
  () => chatList.value.length,
  () => {
    setTimeout(() => {
      scrollToBottom('smooth');
    }, 1);
  },
);

defineExpose({
  chatClose,
});
</script>

<template>
  <div class="assistantBot">
    <div :class="['chat_icon', 'draggleLayer']">
      <div @click="toggleChat">
        <div :class="['expand', showAssistantChat ? 'active' : 'hide']">
          <SVG.AssistantsExpand />
        </div>
        <div :class="['da', !showAssistantChat ? 'active' : 'hide']">
          <SVG.AssistantsFloat />
        </div>

        <div :id="StepsID.assistant" class="tourBox" />
        <img
          :style="{ width: '1.1rem' }"
          :src="genStaticUrl('eraImages/assisant-frame-new.png')"
          alt=""
        />
      </div>
    </div>

    <div
      v-if="chatDisplay"
      class="assistant_container"
      :style="{ transform: showAssistantChat ? 'scale(1)' : 'scale(0)' }"
      @click.stop
    >
      <div class="assistant_container_head">
        <img :src="DTS_AI_IMG" alt="dtsLogo" />
        <SVG.AssistantsPlus class="assistant_container_head_icon" @click="closeChat" />
      </div>

      <div class="assistant_content">
        <div ref="assistantContent" class="assistant_content_box">
          <div ref="assistantBox">
            <div v-for="(item, index) in chatList" :key="index">
              <div v-if="item.type === 'firstMayAsk'">
                <div v-if="mayAskQuestions.length" class="youMayAsk">
                  <div class="askTitle">
                    <div>
                      <Typography.Text :size="16" :weight="400"
                        >You may want to ask:</Typography.Text
                      >
                    </div>
                    <div class="refreshSvg" @click="refreshQustion">
                      <SVG.AssistantRefresh />
                      <Typography.Text :size="16" :weight="400">Refresh</Typography.Text>
                    </div>
                  </div>
                  <div class="askContent">
                    <div
                      v-for="(ques, questionIndex) in mayAskQuestions"
                      :key="ques.id ?? `${ques.questionContent}-${questionIndex}`"
                      class="quest"
                      @click="clickPromptOption(item, ques.questionContent)"
                    >
                      <Typography.Text :size="14" :weight="300">
                        {{ ques.questionContent }}
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                :class="['assistant_chat', item.pos === 'right' ? 'assistant_chat_self' : '']"
              >
                <div v-if="item.pos === 'left'" class="assistant_chat_avatar">
                  <img :src="genStaticUrl('eraImages/bot.png')" alt="" />
                </div>

                <div class="assistant_chat_text">
                  <SVG.AssistantTriangle
                    v-if="item.pos === 'left'"
                    class="assistant_chat_triangle"
                  />
                  <span v-if="item.type === 'begin'" class="assistant_chat_title">
                    Hi {{ username }}
                  </span>

                  <div v-if="item.type === 'sorry'">{{ item.content }}</div>
                  <div v-else>
                    <div class="content" v-html="item.content"></div>

                    <div v-if="shouldShowLoading(item, index)" class="assistant_loading_container">
                      <LoadingUI />
                      <div class="stopButton" @click="stopReply">
                        <SVG.AssistantPause />
                        <Typography.Text :size="14" :weight="400" :color="PRIMARY_COLOR">
                          Pause
                        </Typography.Text>
                      </div>
                    </div>

                    <div v-if="shouldShowGenerated(item, index)" class="assistant_ai_Generated">
                      <SVG.AssistantAIGenerated />
                      <Typography.Text :size="14" :weight="400" color="#666666">
                        AI Generated
                      </Typography.Text>
                    </div>

                    <div v-if="item.promptOptions?.length" class="assistant_chat_prompt">
                      <div
                        v-for="(option, promptIndex) in item.promptOptions"
                        :key="getPromptKey(option, promptIndex)"
                        :class="['prompt_item', item.promptDone ? 'disabled' : '']"
                        @click="clickPromptOption(item, getPromptLabel(option))"
                      >
                        {{ getPromptLabel(option) }}
                      </div>
                    </div>

                    <div v-if="item.type === 'dislikeReason'" class="assistant_dislike_reason">
                      <NInput
                        type="textarea"
                        show-count
                        :value="item.feedbackReason"
                        :autosize="{ minRows: 4 }"
                        :disabled="item.promptDone"
                        maxlength="280"
                        :theme-overrides="themeOverrides"
                        placeholder="Message..."
                        @input="(val) => (item.feedbackReason = val)"
                      />

                      <NButton
                        class="dislike_reason_submit"
                        :disabled="item.promptDone"
                        @click="feedbackReason(item.feedbackReason, item)"
                      >
                        Submit
                      </NButton>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!(showAssistantLoading || llmLoading) && item.type === 'answer'"
                  class="feedback_btn_box"
                >
                  <SVG.AssistantsLike
                    :active="item.feedback === 'like'"
                    :class="item.feedback ? 'like disabled' : 'like'"
                    @click="feedback(true, item)"
                  />
                  <SVG.AssistantsDislike
                    :active="item.feedback === 'dislike'"
                    :class="item.feedback ? 'dislike disabled' : 'dislike'"
                    @click="handleDislike(item)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="assistant_footer">
        <div class="assistant_footer_text">
          <NInput
            v-model:value="valueRef"
            type="textarea"
            :autosize="{ minRows: 1 }"
            :status="inputStatus"
            :theme-overrides="themeOverrides"
            placeholder="Send me your questions..."
            @keydown="handleAutoCompleteKeydown"
          />

          <div class="assistant_footer_error">
            <span v-if="inputStatus === 'error'" :style="{ color: '#d03050', fontSize: '12px' }">
              Please enter your question.
            </span>
          </div>
        </div>
        <div class="assistant_footer_divide"></div>
        <div class="submit_btn">
          <div
            class="assistant_submit"
            role="button"
            tabindex="0"
            @click="sendQuest"
            @keydown.enter.prevent="sendQuest"
            @keydown.space.prevent="sendQuest"
          >
            <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M27.5 27.5V2.5H15V12.5H2.5V27.5H27.5Z"
                stroke="#3309CD"
                stroke-width="1.5625"
              />
              <path d="M13.125 17.5L10.625 20L13.125 22.5" stroke="#3309CD" stroke-width="1.5625" />
              <path d="M21.25 14.375V20H10.625" stroke="#3309CD" stroke-width="1.5625" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@mixin stable() {
  position: fixed;
  bottom: -0.46rem;
  right: -0.4rem;

  cursor: pointer;
  pointer-events: fill;
  user-select: none;

  &:active {
    transform: scale(0.8);
    transition: transform 0.1s;
    transform-origin: 0.7rem 0.7rem;
  }
}

.assistantBot {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background-color: transparent;
  pointer-events: none;

  .draggleLayer {
    @include stable();

    .da {
      width: 0.485rem;
      height: 0.355rem;
      position: absolute;
      top: 0.34rem;
      left: 0.34rem;
    }

    .expand {
      height: 0.18rem;
      width: 0.36rem;
      position: absolute;
      top: 0.4rem;
      left: 0.38rem;
    }

    .active {
      opacity: 1;
      transform: rotate(0deg);
      transition:
        transform 0.16s linear 0s,
        opacity 0.08s linear 0s;
    }

    .hide {
      opacity: 0;
      transform: rotate(-60deg);
      transition:
        transform 0.16s linear 0s,
        opacity 0.08s linear 0s;
    }
  }

  .tourBox {
    position: absolute;
    left: 0.5rem;
    top: 0.5rem;
    height: 0.6rem;
    width: 0.6rem;
  }

  .miniBotLayer {
    @include stable();
    opacity: 0;
    right: 0.2rem;
    bottom: 0.6rem;

    img {
      width: 0.9rem;
    }
  }

  .botShow {
    opacity: 1;
    animation: show 0.8s ease;
  }

  .botClose {
    opacity: 0;
    animation: close 0.8s ease;
  }

  .botOutWin {
    transition: all 0.8s ease;
    transform: translate(2rem);
  }

  .botInWin {
    transition: all 0.8s ease;
    transform: translate(0);
  }

  .chat_icon {
    pointer-events: fill;
    height: 1rem;
    opacity: 1;
    position: fixed;
    right: 0.2rem;
    bottom: 0.1rem;
    background-size: contain;
    background-repeat: no-repeat;
    overflow: hidden;
    z-index: 100;
  }

  .assistant_container {
    pointer-events: fill;
    position: absolute;
    display: flex;
    width: 6.24rem;
    height: 7.38rem;
    right: 0.2rem;
    bottom: 1.2rem;
    border-radius: 10px 0 0 10px;
    background: linear-gradient(180.83deg, #ffffff -42.41%, #f4fbff 86.7%);
    overflow: hidden;
    flex-direction: column;
    transform-origin: 5.52rem 6.84rem;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px 0 rgba(16, 16, 16, 0.15);
    z-index: 900;

    :deep(.v-binder-follower-content) {
      width: 5rem !important;
      margin-left: 0.46rem;
      margin-top: -0.24rem;

      .n-auto-complete-menu {
        box-shadow: 0 -10px 15px rgba(0, 0, 0, 0.03);
      }
    }
  }

  .assistant_container_head {
    background-color: #fff;
    width: 100%;
    height: 0.75rem;
    position: relative;
    line-height: 0.65rem;
    font-size: 0.24rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    padding: 0 0.32rem;

    img {
      height: 0.28rem;
    }
  }

  .assistant_container_head_icon {
    cursor: pointer;
    position: fixed;
    right: 0.2rem;
    width: 0.22rem;
  }

  .assistant_content {
    flex: 1;
    padding-top: 0.2rem;
    position: relative;
    overflow: hidden;
  }

  .assistant_content_box {
    height: 100%;
    overflow-y: auto;
    padding: 0 0.62rem 0 0.32rem;
  }

  .youMayAsk {
    height: 1.15rem;
    width: 106%;
    margin-bottom: 0.16rem;
    margin-top: 0.16rem;

    .askTitle {
      line-height: 0.22rem;
      height: 0.22rem;
      width: 100%;
      display: flex;
      justify-content: space-between;

      .refreshSvg {
        cursor: pointer;
        width: 0.84rem;
        height: 0.22rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: var(--dts-primary-color);

        svg {
          width: 0.18rem;
          height: 0.18rem;
        }
      }
    }

    .askContent {
      margin-top: 0.12rem;
      width: 100%;
      height: 0.81rem;
      display: flex;
      justify-content: space-between;

      .quest {
        width: 1.75rem;
        cursor: pointer;
        background-color: #ced2d833;
        border: Mixed solid;
        border-radius: 0.04rem;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .assistant_footer {
    width: 100%;
    border-top: 0.02rem solid #f3f7fd;
    display: flex;
    height: 1.15rem;
    align-items: center;
    justify-content: space-between;
    background-color: #fff;
  }

  .assistant_footer_text {
    max-height: 1.15rem;
    overflow-y: auto;
    width: 4.06rem;

    :deep(.n-input__border),
    :deep(.n-input__state-border) {
      display: none;
    }

    :deep(.n-input-wrapper) {
      padding: 0 0.32rem;
    }
  }

  .assistant_footer_error {
    padding-left: 0.32rem;
  }

  .assistant_footer_divide {
    height: 0.7rem;
    width: 1px;
    background: #f3f3f3;
  }

  .submit_btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .assistant_chat {
    display: flex;
    align-items: center;
    margin-bottom: 0.15rem;
  }

  .assistant_chat_text {
    max-width: 4.3rem;
    padding: 0.2rem;
    background-color: rgba(243, 247, 253, 1);
    border-radius: 4px;
    font-family: BMWTypeNext, serif;
    font-size: 0.12rem;
    position: relative;
    color: #262626;

    .content {
      font-size: 0.16rem;
      box-sizing: border-box;
      overflow-wrap: break-word;
      font-weight: 400;
    }
  }

  .assistant_chat_triangle {
    position: absolute;
    left: 0;
    top: 0.25rem;
    transform: translate3d(-100%, -50%, 0);
  }

  .assistant_chat_title {
    display: inline-block;
    font-size: 0.24rem;
    font-weight: 700;
    font-family: BMWTypeNext, serif;
    background: linear-gradient(
      to right,
      rgba(255, 0, 186, 1) -20%,
      rgba(149, 18, 213, 1) 20%,
      rgba(96, 1, 181, 1) 60%,
      rgba(64, 33, 235, 1) 100%
    );
    background-clip: text;
    color: transparent;
    margin-bottom: 0.16rem;
  }

  .assistant_chat_prompt {
    margin-top: 0.16rem;

    .prompt_item {
      cursor: pointer;
      margin-top: 0.08rem;
      height: 0.32rem;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #fff;
      font-size: 0.14rem;
      font-family: BMWTypeNext, serif;
      font-weight: 300;
      color: #262626;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

      &.disabled {
        opacity: 0.5;
        cursor: no-drop;
      }
    }

    .prompt_item:not(.disabled):hover {
      border: 1px solid var(--dts-primary-color);
    }
  }

  .assistant_dislike_reason {
    width: 3.9rem;

    :deep(.n-input) {
      margin-top: 0.16rem;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
      overflow-y: auto;
      max-height: 0.86rem;
      font-size: 0.12rem;
    }

    .dislike_reason_submit {
      width: 100%;
      height: 0.3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid var(--dts-primary-color);
      color: var(--dts-primary-color);
      font-size: 12px;
      font-weight: 400;
      margin-top: 0.16rem;

      &:hover {
        color: white;
        background-color: var(--dts-primary-color);
      }
    }
  }

  .assistant_chat_avatar {
    position: relative;
    width: 0.5rem;
    height: 0.5rem;
    min-width: 0.5rem;
    min-height: 0.5rem;
    border-radius: 50%;
    margin-right: 0.14rem;
    background-size: contain;
    background-color: #e4efff;
    overflow: hidden;
    align-self: flex-start;

    img {
      position: absolute;
      width: 0.5rem;
      bottom: 0;
    }
  }

  .assistant_chat_self {
    flex-direction: row-reverse;
  }

  .assistant_chat_self .assistant_chat_text {
    background-color: #e4efff;
    margin-right: -0.36rem;
  }

  .feedback_btn_box {
    width: 0.18rem;
    min-width: 0.18rem;
    margin-left: 0.04rem;
    display: flex;
    flex-direction: column;
    align-self: flex-end;
    row-gap: 0.04rem;

    svg {
      cursor: pointer;

      &.disabled {
        cursor: no-drop;
      }
    }

    .like.active {
      color: #0f5ef7;
    }

    .dislike.active {
      color: #ef4343;
    }
  }

  .assistant_submit {
    cursor: pointer;
    width: 0.94rem;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    background-size: contain;

    svg {
      width: 0.38rem;
    }
  }

  .assistant_loading_container {
    width: 4rem;
    height: 0.28rem;
    display: flex;
    justify-content: right;
    gap: 1.35rem;
    align-items: center;

    .stopButton {
      background: #e4efff;
      width: 0.79rem;
      height: 0.27rem;
      border-radius: 2px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.06rem;
      cursor: pointer;

      svg {
        color: var(--dts-primary-color);
        width: 0.16rem;
        height: 0.16rem;
      }
    }
  }

  .assistant_ai_Generated {
    padding-right: 0.2rem;
    width: 4rem;
    height: 0.28rem;
    display: flex;
    justify-content: right;
    align-items: center;
    gap: 0.08rem;

    svg {
      color: #666666;
      width: 0.16rem;
      height: 0.16rem;
    }
  }

  .assistant_loading,
  .assistant_loading > div {
    position: relative;
    box-sizing: border-box;
  }

  .assistant_loading {
    display: block;
    font-size: 0;
    color: #572df3;
    width: 54px;
    height: 18px;
  }

  .assistant_loading.la-dark {
    color: #333;
  }

  .assistant_loading > div {
    display: inline-block;
    float: none;
    background-color: currentColor;
    border: 0 solid currentColor;
    width: 10px;
    height: 10px;
    margin: 4px;
    border-radius: 100%;
    animation: ball-beat 0.7s -0.15s infinite linear;
  }

  .assistant_loading > div:nth-child(2n-1) {
    animation-delay: -0.5s;
  }

  @keyframes ball-beat {
    50% {
      opacity: 0.2;
      transform: scale(0.75);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  .assistant_hot_quest_card {
    margin: 0.06rem auto 0;
    box-shadow: 0 2px 8px 0 rgba(16, 16, 16, 0.15);
    padding: 0 0.12rem;
    border-radius: 0.04rem;
    color: rgba(87, 45, 243, 1);
  }

  .assistant_hot_quest_card_item {
    padding: 0.12rem 0;
    border-bottom: 0.5px solid rgb(226, 226, 226);
    cursor: pointer;
  }

  .assistant_hot_quest_card_item:last-child {
    border-bottom: none;
  }

  .shrinkBtn {
    position: absolute;
    left: 1.48rem;
    top: 0.32rem;
    width: 0.25rem;
    height: 0.25rem;
    border-radius: 50%;
    background: #cdb3c5;
    display: flex;
    justify-content: center;
    align-items: center;

    .dashBar {
      width: 0.15rem;
      height: 0.03rem;
      background: #fff;
    }

    &:hover {
      opacity: 0.4;
    }

    &:active {
      opacity: 0.8;
    }
  }
}

@keyframes show {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes close {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}
</style>
